import { useState } from 'react';
import type { BaselineSeriesPoint, EventFlagDay } from '@halo/web-types';
import { classifyDay, confidenceLabel } from '@halo/web-types';
import type { ControlsState } from './Controls';
import { FlagEditor } from './FlagEditor';
import { useRawFlags } from './useHaloV2';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm';
const usd = (v: number) => `$${Math.round(v).toLocaleString()}`;

interface DayDetailCardProps {
  state: ControlsState;
  point: BaselineSeriesPoint | null;
  eventDays: EventFlagDay[];
}

export function DayDetailCard({ state, point, eventDays }: DayDetailCardProps) {
  const [editing, setEditing] = useState(false);
  const rawFlagsQuery = useRawFlags(state);

  if (!point) {
    return (
      <div className={cardCls}>
        <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-1">Day detail</h3>
        <p className="text-xs text-gray-400">Click a day on the chart.</p>
      </div>
    );
  }

  const eventDay = eventDays.find((d) => d.date === point.date);
  const rawRow = rawFlagsQuery.data?.find((r) => r.date === point.date);

  return (
    <div className={cardCls}>
      <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">{point.date}</h3>
      <dl className="space-y-0.5 text-xs">
        <Row k="Actual" v={usd(point.actualRevenue)} />
        <Row k="Selected baseline" v={usd(point.selectedBaseline)} />
        <Row k="Raw baseline" v={usd(point.rawBaseline)} />
        <Row
          k="Media-adjusted"
          v={point.mediaAdjustedBaseline === null ? 'n/a' : usd(point.mediaAdjustedBaseline)}
        />
        <Row k="Positive halo" v={usd(point.positiveHalo)} />
        <Row k="Negative residual" v={usd(point.negativeResidual)} />
        <Row k="Media pressure" v={point.mediaPressureScore.toFixed(1)} />
        <Row
          k="Confidence"
          v={`${point.baselineConfidence.toFixed(0)} (${confidenceLabel(point.baselineConfidence)})`}
        />
        <Row k="Confounded" v={point.isConfounded ? 'yes' : 'no'} />
        <Row
          k="Event weight"
          v={
            eventDay
              ? `${eventDay.weight}${eventDay.confounded ? ' (confounded)' : ''}`
              : '1.0 (normal)'
          }
        />
        <Row k="Day class" v={classifyDay(point.mediaPressureScore, point.isConfounded)} />
      </dl>
      <button
        type="button"
        className="mt-2 rounded-md border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        onClick={() => setEditing((e) => !e)}
      >
        {editing ? 'Close editor' : 'Edit flags'}
      </button>
      {editing ? (
        <FlagEditor
          key={point.date}
          storeId={state.storeId}
          date={point.date}
          existing={rawRow}
          onSaved={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
      <dd className="font-semibold tabular-nums text-gray-800 dark:text-gray-100">{v}</dd>
    </div>
  );
}
